import { useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { HomeView } from "@/components/HomeView";
import { ProjectView } from "@/components/ProjectView";
import { JudgeView } from "@/components/JudgeView";
import { CodeView } from "@/components/CodeView";
import { useWorkspaceViewContext } from "@/hooks/useWorkspaceViewContext";

const WorkspacePaneFallback = ({
	title,
	description,
}: {
	title: string;
	description: string;
}) => {
	const navigate = useNavigate();

	return (
		<div className="flex h-full items-center justify-center p-8">
			<div className="max-w-md rounded-lg border border-dashed border-border p-8 text-center">
				<h2 className="text-2xl font-semibold text-foreground">{title}</h2>
				<p className="mt-2 text-muted-foreground">{description}</p>
				<Button className="mt-6" onClick={() => navigate("/workspace")}>
					Back to workspace
				</Button>
			</div>
		</div>
	);
};

export const WorkspaceHomeRoute = () => {
	const {
		projects,
		codes,
		judges,
		datasets,
		createProject,
		createCode,
		createJudge,
		createDataset,
		profile,
		user,
	} = useWorkspaceViewContext();
	const navigate = useNavigate();

	return (
		<HomeView
			projects={projects}
			agents={codes}
			judges={judges}
			datasets={datasets}
			onSelectProject={(id) => navigate(`/workspace/evals/${id}`)}
			onSelectAgent={(id) => navigate(`/workspace/codes/${id}`)}
			onSelectJudge={(id) => navigate(`/workspace/judges/${id}`)}
			onSelectDataset={(id) => navigate(`/dataset/${id}`)}
			onNewProject={createProject}
			onNewAgent={createCode}
			onNewJudge={createJudge}
			onNewDataset={createDataset}
			username={profile?.display_name || undefined}
			user={user}
		/>
	);
};

export const WorkspaceProjectRoute = () => {
	const params = useParams();
	const evalId = params["evalId"] || params["projectId"];
	const { projects, refreshWorkspaceData } = useWorkspaceViewContext();

	if (!evalId) {
		return (
			<WorkspacePaneFallback
				title="Eval not found"
				description="Select an eval from the sidebar or return to the workspace home."
			/>
		);
	}

	const project = projects.find((item) => item.id === evalId);
	if (!project) {
		return (
			<WorkspacePaneFallback
				title="Eval not found"
				description="The requested eval could not be found."
			/>
		);
	}

	return (
		<ProjectView
			project={project}
			userEmail={project.owner_email}
			onProjectUpdate={() => refreshWorkspaceData()}
		/>
	);
};

export const WorkspaceJudgeRoute = () => {
	const { judgeId } = useParams();
	const navigate = useNavigate();
	const { judges, refreshWorkspaceData, user } = useWorkspaceViewContext();

	if (!judgeId) {
		return (
			<WorkspacePaneFallback
				title="Judge not found"
				description="Select a judge from the sidebar or return to the workspace home."
			/>
		);
	}

	const judge = judges.find((item) => item.id === judgeId);
	if (!judge) {
		return (
			<WorkspacePaneFallback
				title="Judge not found"
				description="The requested judge could not be found."
			/>
		);
	}

	return (
		<JudgeView
			judge={judge}
			userEmail={user?.email}
			onUpdate={refreshWorkspaceData}
			onDelete={() => {
				refreshWorkspaceData();
				navigate("/workspace");
			}}
		/>
	);
};

export const WorkspaceCodeRoute = () => {
	const { codeId } = useParams();
	const navigate = useNavigate();
	const { codes, refreshWorkspaceData, user, userIsAdmin } =
		useWorkspaceViewContext();

	if (!codeId) {
		return (
			<WorkspacePaneFallback
				title="Agent not found"
				description="Select an agent from the sidebar or return to the workspace home."
			/>
		);
	}

	const code = codes.find((item) => item.id === codeId);
	if (!code) {
		return (
			<WorkspacePaneFallback
				title="Agent not found"
				description="The requested agent could not be found."
			/>
		);
	}

	return (
		<CodeView
			code={code}
			userEmail={code.owner_email ?? user?.email}
			isAdmin={userIsAdmin}
			onUpdate={refreshWorkspaceData}
			onDelete={() => {
				refreshWorkspaceData();
				navigate("/workspace");
			}}
		/>
	);
};
